import { revalidateLogic } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useResetFormOnLoad } from '~/hooks/useResetFormOnLoad';

import { type UUID } from '~/types/common';

import {
  type CreateTaxCodesPayload,
  type TaxCode,
  type UpdateTaxCodePayload,
  useMutationCreateTaxCodes,
  useMutationUpdateTaxCode,
  useQueryTaxCode,
} from '~/data/taxCode';

import ToastService from '~/services/toast.service';

import { logFormChangesAndErrors } from '@vestigas/utils';
import { i18n, log } from '~/utils';

import { useAppForm } from '~/ui/molecules/TanstackForm';

const requiredCompanyError = i18n.t(($) => $.form.errors.companyIsRequired, {
  ns: 'taxCodes',
});
const rateInvalidError = i18n.t(($) => $.form.errors.rateInvalid, {
  ns: 'taxCodes',
});
const rateRequiredError = i18n.t(($) => $.form.errors.rateIsRequired, {
  ns: 'taxCodes',
});
const rateTooLowError = i18n.t(($) => $.form.errors.rateTooLow, {
  ns: 'taxCodes',
});
const rateTooHighError = i18n.t(($) => $.form.errors.rateTooHigh, {
  ns: 'taxCodes',
});

export const zodTaxCodeFormSchema = z
  .object({
    applyToAllCompaniesInAccount: z.boolean(),
    code: z
      .string()
      .trim()
      .max(
        64,
        i18n.t(($) => $.form.errors.codeTooLong, { ns: 'taxCodes' }),
      ),
    companyIds: z
      .array(
        z.uuid({
          message: requiredCompanyError,
        }),
      )
      .max(
        1000,
        i18n.t(($) => $.form.errors.tooManyCompanies, {
          ns: 'taxCodes',
        }),
      ),
    companyName: z.string(),
    isReverseCharge: z.boolean(),
    name: z
      .string()
      .trim()
      .min(1)
      .max(
        255,
        i18n.t(($) => $.form.errors.nameTooLong, { ns: 'taxCodes' }),
      ),
    rate: z
      // Not nullable: a nullable schema marks the field as optional.
      .number({
        error: ({ input }) =>
          input === null || input === undefined
            ? rateRequiredError
            : rateInvalidError,
      })
      .min(0, rateTooLowError)
      .max(100, rateTooHighError),
  })
  // `when` keeps the cross-field checks running even while another field is
  // invalid; by default zod skips refinements once any field has failed, which
  // would hide this error until the other fields are fixed.
  .refine(
    (value) =>
      value.applyToAllCompaniesInAccount || (value.companyIds?.length ?? 0) > 0,
    {
      error: requiredCompanyError,
      path: ['companyIds'],
      when: () => true,
    },
  )
  .refine((value) => !(value.isReverseCharge && value.rate === 0), {
    error: i18n.t(($) => $.form.errors.reverseChargeRateZero, {
      ns: 'taxCodes',
    }),
    path: ['rate'],
    when: ({ value }) => typeof (value as { rate?: unknown }).rate === 'number',
  });

export type TaxCodeFormValues = z.infer<typeof zodTaxCodeFormSchema>;

export const TAX_CODE_FORM_DEFAULT_VALUES: TaxCodeFormValues = {
  applyToAllCompaniesInAccount: false,
  code: '',
  companyIds: [],
  companyName: '',
  isReverseCharge: false,
  name: '',
  // Empty until entered; the field stores null for a blank input.
  rate: null as unknown as number,
};

function getDefaultValues(taxCode: TaxCode | undefined): TaxCodeFormValues {
  if (!taxCode) {
    return TAX_CODE_FORM_DEFAULT_VALUES;
  }

  return {
    applyToAllCompaniesInAccount: false,
    code: taxCode.code ?? '',
    companyIds: [taxCode.company.id],
    companyName: taxCode.company.name ?? '',
    isReverseCharge: taxCode.isReverseCharge,
    name: taxCode.name,
    rate: Number(taxCode.rate),
  };
}

export function buildCreateTaxCodesPayload(
  value: TaxCodeFormValues,
): CreateTaxCodesPayload {
  return {
    applyToAllCompaniesInAccount: value.applyToAllCompaniesInAccount,
    code: value.code || null,
    companyIds: value.applyToAllCompaniesInAccount ? null : value.companyIds,
    isReverseCharge: value.isReverseCharge,
    name: value.name,
    rate: value.rate.toString(),
  };
}

export function buildUpdateTaxCodePayload(
  value: TaxCodeFormValues,
): UpdateTaxCodePayload {
  return {
    code: value.code || null,
    isReverseCharge: value.isReverseCharge,
    name: value.name,
    rate: value.rate.toString(),
  };
}

type P = {
  readonly onSuccess: () => void;
  readonly taxCodeId?: UUID;
};

export function useTaxCodeForm({ onSuccess, taxCodeId }: P) {
  const { t } = useTranslation('taxCodes');
  const isEditMode = Boolean(taxCodeId);
  const {
    data: taxCode,
    isError: queryError,
    isLoading,
  } = useQueryTaxCode(taxCodeId);
  const createMutation = useMutationCreateTaxCodes();
  const updateMutation = useMutationUpdateTaxCode();
  const defaultValues = getDefaultValues(taxCode);

  const form = useAppForm({
    defaultValues,
    async onSubmit({ value }) {
      const validatedValue = zodTaxCodeFormSchema.parse(value);

      if (taxCodeId) {
        if (!taxCode) return;

        await updateMutation.mutateAsync({
          payload: buildUpdateTaxCodePayload(validatedValue),
          taxCodeId,
        });
        ToastService.success(t(($) => $.form.toast.updateSuccess));
      } else {
        await createMutation.mutateAsync(
          buildCreateTaxCodesPayload(validatedValue),
        );
        ToastService.success(t(($) => $.form.toast.createSuccess));
      }

      onSuccess();
    },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: zodTaxCodeFormSchema,
    },
    ...logFormChangesAndErrors('TaxCodeForm', log),
  });

  useResetFormOnLoad(form, defaultValues, !taxCodeId || isLoading);

  return {
    form,
    isEditMode,
    isLoading,
    isPending: createMutation.isPending || updateMutation.isPending,
    mutationError: createMutation.error || updateMutation.error,
    queryError,
  };
}
